import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Field,
  FieldLabel,
  FieldSelect,
  Pagination,
  SearchInput,
  StatusBadge,
  toneForStatus,
  type DataTableColumn,
} from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ColumnPicker } from '../../../components/resource-list/column-picker.js';
import { useAppStore } from '../../../lib/state/store.js';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import {
  resolveVisibleColumns,
  type ColumnDescriptor,
} from '../../../lib/resource-list/columns.js';
import { useSoftDeleteGateway } from '../api/mutations.js';
import { useGateways } from '../api/queries.js';
import { CHANNEL_OPTIONS, type GatewayRow } from '../api/schemas.js';

const ALL_CHANNELS = [{ value: '', label: 'All channels' }, ...CHANNEL_OPTIONS];

const DEFAULT_SORT = { columnId: 'createdAt', direction: 'desc' } as const;
const PAGE_SIZE = 10;
const FIRST_PAGE = 1;

const COLUMNS: readonly ColumnDescriptor<GatewayRow>[] = [
  {
    id: 'name',
    header: 'Gateway',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'provider'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/notifications/${row.id}`}
        secondary={row.provider ?? null}
      />
    ),
  },
  {
    id: 'channel',
    header: 'Channel',
    sortable: true,
    fields: ['channel'],
    cell: (row) => row.channel,
  },
  {
    id: 'provider',
    header: 'Provider',
    sortable: true,
    fields: ['provider'],
    cell: (row) => row.provider,
  },
  {
    id: 'priority',
    header: 'Priority',
    sortable: true,
    fields: ['priority'],
    cell: (row) => (row.priority === undefined ? null : String(row.priority)),
  },
  {
    id: 'default',
    header: 'Default',
    fields: ['isDefault'],
    cell: (row) => (row.isDefault ? 'Yes' : 'No'),
  },
  {
    id: 'status',
    header: 'Status',
    fields: ['isActive'],
    cell: (row) =>
      row.isActive === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.isActive ? 'ACTIVE' : 'INACTIVE')}>
          {row.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    cell: (row) =>
      row.createdAt === undefined ? null : new Date(row.createdAt).toLocaleDateString('en-AU'),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: GatewayRow) => <GatewayRowActions row={row} />,
  },
];

const RESOURCE = 'notificationGateways';

function GatewayRowActions({ row }: { readonly row: GatewayRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteGateway();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/notifications/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/notifications/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete gateway"
        description={`${label} will be removed. Its audit history and any dispatches that used it are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

/** Filters the fetched rows by the user's search term. */
function filterRows(rows: readonly GatewayRow[], q: string): readonly GatewayRow[] {
  const needle = q.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) => (row.name ?? '').toLowerCase().includes(needle));
}

/** Sorts the rows using the requested column and direction. */
function sortRows(
  rows: readonly GatewayRow[],
  sortBy: string,
  sortDirection: 'asc' | 'desc',
): readonly GatewayRow[] {
  const sorted = [...rows];
  const direction = sortDirection === 'asc' ? 1 : -1;

  sorted.sort((left, right) => {
    const leftValue = left[sortBy as keyof GatewayRow];
    const rightValue = right[sortBy as keyof GatewayRow];

    if (leftValue === rightValue) return 0;
    if (leftValue === undefined || leftValue === null) return direction;
    if (rightValue === undefined || rightValue === null) return -direction;

    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      return leftValue.localeCompare(rightValue) * direction;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
      return (Number(leftValue) - Number(rightValue)) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });

  return sorted;
}

export function NotificationGatewayListPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const channel = params.get('channel') ?? '';
  const sortBy = params.get('sortBy') ?? DEFAULT_SORT.columnId;
  const sortDirection = (params.get('sortDirection') ?? DEFAULT_SORT.direction) as 'asc' | 'desc';

  const columnSelection = useAppStore((state) => state.prefs.columns[RESOURCE]);

  const page = Math.max(
    FIRST_PAGE,
    Number.isNaN(Number(params.get('page'))) ? FIRST_PAGE : Number(params.get('page')),
  );

  const query = useGateways(channel === '' ? undefined : channel);

  const rows = useMemo(() => {
    const filtered = filterRows(query.data ?? [], q);
    return sortRows(filtered, sortBy, sortDirection);
  }, [query.data, q, sortBy, sortDirection]);

  const visibleColumns: readonly DataTableColumn<GatewayRow>[] = useMemo(
    () => resolveVisibleColumns(COLUMNS, columnSelection),
    [columnSelection],
  );

  const totalItems = rows.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  const meta = useMemo(
    () => ({
      page: safePage,
      pageSize: PAGE_SIZE,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > FIRST_PAGE,
    }),
    [safePage, totalItems, totalPages],
  );

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <PermissionGuard permission={PERMISSIONS.NOTIFICATION_READ} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-page-title font-semibold">Notification gateways</h1>
            <p className="text-body text-muted-foreground">
              Email and SMS provider connections the platform sends messages through.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ColumnPicker resource={RESOURCE} columns={COLUMNS} />
            <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
              <Button asChild>
                <Link to="/notifications/new">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add gateway
                </Link>
              </Button>
            </PermissionGuard>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SearchInput
            label="Search gateways"
            className="h-10 md:mt-5"
            defaultValue={q}
            onSearch={(term) => {
              update({ q: term, page: undefined });
            }}
          />
          <Field>
            <FieldLabel>Channel</FieldLabel>
            <FieldSelect
              value={channel}
              onValueChange={(value) => {
                update({ channel: value, page: undefined });
              }}
              options={ALL_CHANNELS}
              placeholder="All channels"
            />
          </Field>
        </div>

        <DataTable
          caption="Notification gateways"
          columns={visibleColumns}
          rows={pagedRows}
          rowKey={(row) => row.id}
          loading={query.isPending}
          error={errorTextFor(query.error) ?? undefined}
          onRetry={() => {
            void query.refetch();
          }}
          sort={{ columnId: sortBy, direction: sortDirection }}
          onSortChange={(next) => {
            update({ sortBy: next.columnId, sortDirection: next.direction });
          }}
          emptyTitle="No gateways found"
          emptyDescription="Add a gateway to start sending notifications."
        />

        {!query.isPending && totalItems > 0 && (
          <Pagination
            meta={meta}
            itemNoun={{ one: 'gateway', many: 'gateways' }}
            onPageChange={(next) => {
              update({ page: String(next) });
            }}
          />
        )}
      </section>
    </PermissionGuard>
  );
}
